import React, { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuthStore } from '../../lib/store-auth';
import { ProfileAvatar } from '../profile/ProfileAvatar';
import './leaderboard.scss';

interface LeaderboardEntry {
    userId: string;
    fullName: string;
    avatarUrl?: string;
    totalScore: number;
    simulationsCompleted: number;
}

export const Leaderboard: React.FC = () => {
    const { user } = useAuthStore();
    const [leaderboardData, setLeaderboardData] = useState<LeaderboardEntry[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchLeaderboardData = async () => {
            if (!user?.institution_id || !supabase) return;

            try {
                // 1. Kurumdaki tüm kullanıcıları çek
                const { data: users, error: usersError } = await supabase
                    .from('profiles')
                    .select('id, full_name, avatar_url')
                    .eq('institution_id', user.institution_id);

                if (usersError) throw usersError;

                if (!users || users.length === 0) {
                    setLeaderboardData([]);
                    return;
                }

                // 2. Bu kullanıcıların raporlarını çek
                const userIds = users.map(u => u.id);
                const { data: reports, error: reportsError } = await supabase
                    .from('simulation_reports')
                    .select('user_id, simulation_id, score')
                    .in('user_id', userIds);

                if (reportsError) throw reportsError;

                // 3. Puanlama Mantığı: Her simülasyon için en yüksek puanı al ve topla
                const leaderboard: LeaderboardEntry[] = users.map(u => {
                    const userReports = reports?.filter(r => r.user_id === u.id) || [];

                    // Simülasyon bazında grupla ve max puanı bul
                    const bestScoresBySimulation: Record<string, number> = {};

                    userReports.forEach(r => {
                        const currentBest = bestScoresBySimulation[r.simulation_id] || 0;
                        if ((r.score || 0) > currentBest) {
                            bestScoresBySimulation[r.simulation_id] = r.score || 0;
                        }
                    });

                    // Toplam puanı hesapla
                    const totalScore = Object.values(bestScoresBySimulation).reduce((sum, score) => sum + score, 0);

                    return {
                        userId: u.id,
                        fullName: u.full_name || 'İsimsiz Kullanıcı',
                        avatarUrl: u.avatar_url,
                        totalScore: totalScore,
                        simulationsCompleted: Object.keys(bestScoresBySimulation).length
                    };
                });

                // Sort users by score (descending)
                const mockUsers: LeaderboardEntry[] = [
                    { userId: 'mock1', fullName: 'Ahmet Yılmaz', totalScore: 2450, simulationsCompleted: 28, avatarUrl: undefined },
                    { userId: 'mock2', fullName: 'Ayşe Demir', totalScore: 2320, simulationsCompleted: 25, avatarUrl: undefined },
                    { userId: 'mock3', fullName: 'Mehmet Kaya', totalScore: 2150, simulationsCompleted: 22, avatarUrl: undefined },
                    { userId: 'mock4', fullName: 'Zeynep Çelik', totalScore: 1980, simulationsCompleted: 19, avatarUrl: undefined },
                    { userId: 'mock5', fullName: 'Can Yıldız', totalScore: 1850, simulationsCompleted: 16, avatarUrl: undefined },
                ];

                const finalLeaderboard = [...leaderboard, ...mockUsers].sort((a, b) => b.totalScore - a.totalScore);

                setLeaderboardData(finalLeaderboard);

            } catch (err: any) {
                console.error("Liderlik tablosu yüklenirken hata:", err);
                setError("Liderlik tablosu yüklenirken bir hata oluştu.");
            } finally {
                setIsLoading(false);
            }
        };

        fetchLeaderboardData();
    }, [user?.institution_id]);

    if (isLoading) return <div className="loading-spinner">Yükleniyor...</div>;
    if (error) return <div className="error-message">{error}</div>;

    const topThree = leaderboardData.slice(0, 3);
    const restOfUsers = leaderboardData.slice(3);

    // Helper for Podium Item
    const PodiumItem = ({ entry, rank }: { entry: LeaderboardEntry; rank: 1 | 2 | 3 }) => (
        <div className={`podium-place place-${rank}`}>
            <div className="avatar-container">
                <ProfileAvatar url={entry.avatarUrl} name={entry.fullName} size={rank === 1 ? 'xl' : 'lg'} />
                <div className="rank-badge">{rank}</div>
            </div>
            <div className="user-info">
                <div className="name">{entry.fullName} {entry.userId === user?.id && '(Siz)'}</div>
                <div className="score">{entry.totalScore} Puan</div>
            </div>
            <div className="pedestal">
                {rank}
            </div>
        </div>
    );

    return (
        <div className="leaderboard-container">

            {/* Podium Section */}
            {topThree.length > 0 && (
                <div className="podium-section">
                    {/* 2nd Place (Left) */}
                    {topThree[1] && <PodiumItem entry={topThree[1]} rank={2} />}

                    {/* 1st Place (Center) */}
                    {topThree[0] && <PodiumItem entry={topThree[0]} rank={1} />}

                    {/* 3rd Place (Right) */}
                    {topThree[2] && <PodiumItem entry={topThree[2]} rank={3} />}
                </div>
            )}

            {/* Rest of Users List */}
            {restOfUsers.length > 0 && (
                <div className="list-section">
                    <table>
                        <thead>
                            <tr>
                                <th style={{ width: '80px' }}>SIRA</th>
                                <th>KULLANICI</th>
                                <th style={{ textAlign: 'center' }}>TAMAMLANAN SİM.</th>
                                <th style={{ textAlign: 'right' }}>TOPLAM PUAN</th>
                            </tr>
                        </thead>
                        <tbody>
                            {restOfUsers.map((entry, index) => {
                                const rank = index + 4;
                                const isCurrentUser = entry.userId === user?.id;

                                return (
                                    <tr key={entry.userId} style={{ background: isCurrentUser ? '#eff6ff' : 'transparent' }}>
                                        <td>
                                            <div style={{
                                                width: '32px', height: '32px', borderRadius: '50%',
                                                background: '#f3f4f6', color: '#6b7280',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600
                                            }}>
                                                {rank}
                                            </div>
                                        </td>
                                        <td>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                <ProfileAvatar url={entry.avatarUrl} name={entry.fullName} size="sm" />
                                                <span style={{ fontWeight: 500, color: '#111827' }}>{entry.fullName} {isCurrentUser && '(Siz)'}</span>
                                            </div>
                                        </td>
                                        <td style={{ textAlign: 'center', color: '#6b7280' }}>
                                            {entry.simulationsCompleted}
                                        </td>
                                        <td style={{ textAlign: 'right' }}>
                                            <span style={{ fontWeight: 700, color: '#4b5563', fontSize: '15px' }}>{entry.totalScore}</span>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {leaderboardData.length === 0 && (
                <div className="empty-state" style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                    Henüz veri bulunmuyor.
                </div>
            )}
        </div>
    );
};
